'use client';

import { useEffect, useRef } from 'react';

import { WELLO_IMAGE_PREFIX_URL } from '@/constants';

const ICONS = [
  `${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/gift-box.png?w=160&q=100&f=auto`,
  `${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/money.png?w=160&q=100&f=auto`,
];

export const PopcornEffect = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const iconIndexRef = useRef(0);

  const createSingleFirework = (delay: number = 0, slotX: number) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const icon = ICONS[iconIndexRef.current];
    iconIndexRef.current = (iconIndexRef.current + 1) % ICONS.length;
    // slotX 기반으로 시작 위치 결정 (겹침 방지)
    const startX = slotX + (Math.random() - 0.5) * 30;
    const randomDriftX =
      (Math.random() * 240 - 120) * (Math.random() * 0.5 + 0.8);
    const maxRotate =
      (Math.random() * 15 + 10) * (Math.random() > 0.5 ? 1 : -1);

    const img = document.createElement('img');
    img.src = icon;
    img.alt = '';
    img.style.cssText = `
      position: absolute;
      bottom: 0;
      left: ${startX}px;
      width: 80px;
      height: 80px;
      object-fit: contain;
      pointer-events: none;
      will-change: transform, opacity;
      opacity: 0;
    `;
    container.appendChild(img);

    setTimeout(() => {
      const startTime = performance.now();
      const duration = 2500;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        if (progress >= 1) {
          img.remove();

          return;
        }

        // 실제 물리: y = v0*t - 0.5*g*t^2 (포물선 운동)
        // progress 0~1을 t로 매핑, 정점이 0.35 지점에 오도록
        const peakTime = 0.35;
        const t = progress;

        // 정규화된 포물선: 정점이 peakTime에서 1이 되도록
        const parabola =
          -(1 / (peakTime * (1 - peakTime))) * (t - peakTime) * (t - peakTime) +
          1;

        // Y축: 위로 올라갔다 내려옴
        const translateY = -parabola * 170;

        // X축: easeOut으로 자연스럽게
        const easeOutProgress = 1 - Math.pow(1 - progress, 2);
        const translateX = randomDriftX * easeOutProgress;

        // Scale: 올라갈 때 커지고 내려올 때 작아짐
        const scale = 0.6 + parabola * 0.5;

        // Rotate
        const rotate = maxRotate * progress;

        // Opacity
        let opacity: number;
        if (progress < 0.08) {
          opacity = progress * 12.5;
        } else if (progress > 0.75) {
          opacity = (1 - progress) * 4;
        } else {
          opacity = 1;
        }

        img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale}) rotate(${rotate}deg)`;
        img.style.opacity = Math.max(0, Math.min(1, opacity)).toString();

        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    }, delay);
  };

  const createFireworkBurst = () => {
    // 5~7개 뭉텅이로 발사
    const count = Math.floor(Math.random() * 3) + 5;

    // 슬롯 위치 미리 계산 (겹침 방지)
    const slots = [];
    const slotWidth = 280 / count;
    for (let i = 0; i < count; i++) {
      slots.push(40 + slotWidth * i + slotWidth / 2);
    }
    // 셔플해서 랜덤하게
    slots.sort(() => Math.random() - 0.5);

    for (let i = 0; i < count; i++) {
      // 각각 약간의 딜레이를 줘서 자연스럽게
      createSingleFirework(i * (Math.random() * 100 + 50), slots[i]);
    }

    // 다음 뭉텅이 생성
    timeoutRef.current = setTimeout(
      createFireworkBurst,
      Math.random() * 1500 + 1500,
    );
  };

  useEffect(() => {
    const initialTimeout = setTimeout(createFireworkBurst, 500);

    return () => {
      clearTimeout(initialTimeout);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute -inset-x-100 -bottom-20 -top-300"
      style={{ clipPath: 'inset(0 -100px 40px -100px)' }}
    />
  );
};
